var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/hooks/useRoute.ts
import {
  ConnectorUtils,
  InnerEdge,
  ItemClass,
  NumberUtils,
  PointUtils,
  RectangleUtils,
  Side,
  mapFilterNullish
} from "@ballpark/design-creator";
import { bush } from "isect";
import createGraph from "ngraph.graph";
import { nba } from "ngraph.path";
function edgeVectorsToDict(edgeVectors) {
  return edgeVectors.reduce((acc, edgeVector) => {
    acc[edgeVector.edge] = edgeVector;
    return acc;
  }, {});
}
function createRouteGraph(design, connector, startPoint, endPoint, obstacles = [], edgePadding = ((_a) => (_a = connector.radius) != null ? _a : 16)() * 2, isStart = true, isEnd = true) {
  const { start, end } = connector;
  const lines = [];
  const entryPoints = [];
  const boundsPoints = [];
  const connectionsAreEqual = isStart && isEnd && ConnectorUtils.isConnectionEqual(start, end);
  const illegalIntersects = [];
  const illegalAreas = [];
  const penaltyAreas = [];
  const longVectors = [];
  const boundsVectors = [];
  const addBounds = (bounds, entrySide, entryOffset = 0.5) => {
    illegalAreas.push(bounds);
    const expandedBounds = RectangleUtils.expand(bounds, edgePadding * 2);
    penaltyAreas.push(expandedBounds);
    const edgeVectors = RectangleUtils.getEdgeVectors(bounds, {
      edges: { top: true, right: true, bottom: true, left: true }
    });
    illegalIntersects.push(...edgeVectors);
    boundsVectors.push(edgeVectors);
    const expandedEdgeVectors = RectangleUtils.getEdgeVectors(expandedBounds, {
      edges: {
        hmid: true,
        vmid: true,
        top: true,
        right: true,
        bottom: true,
        left: true
      }
    });
    longVectors.push(...expandedEdgeVectors);
    const expandedLines = expandedEdgeVectors.map(
      (edgeVector) => [edgeVector.start, edgeVector.end]
    );
    boundsPoints.push(...RectangleUtils.getCorners(expandedBounds));
    lines.push(...expandedLines);
    if (entrySide === void 0)
      return;
    const boundsEntryPoints = edgeVectors.filter((edgeVector) => edgeVector.edge === entrySide).map((edgeVector) => ({
      sides: [edgeVector.edge],
      point: PointUtils.interpolate(
        edgeVector.start,
        edgeVector.end,
        entryOffset
      )
    }));
    entryPoints.push(...boundsEntryPoints);
  };
  const addConnection = (point, connection) => {
    if (!connection || ConnectorUtils.isPoint(connection)) {
      boundsPoints.push(point);
      entryPoints.push({
        sides: [Side.Left, Side.Right, Side.Top, Side.Bottom],
        point
      });
      return;
    }
    const bounds = design.getBounds(connection.id, true);
    if (!bounds)
      return;
    addBounds(
      bounds,
      connection.side,
      ConnectorUtils.getConnectionOffset(connection)
    );
  };
  addConnection(startPoint, isStart ? start : void 0);
  if (!connectionsAreEqual) {
    addConnection(endPoint, isEnd ? end : void 0);
  }
  obstacles.forEach((bounds) => addBounds(bounds));
  const boundingBox = RectangleUtils.containing(boundsPoints);
  const boundingLines = RectangleUtils.getEdgeVectors(boundingBox, {
    edges: { top: true, right: true, bottom: true, left: true }
  }).map((edgeVector) => [edgeVector.start, edgeVector.end]);
  lines.push(...boundingLines);
  longVectors.forEach((edgeVector) => {
    if (edgeVector.edge === Side.Top || edgeVector.edge === Side.Bottom || edgeVector.edge === InnerEdge.VMid) {
      lines.push([
        [boundingBox.point[0], edgeVector.start[1]],
        [boundingBox.point[0] + boundingBox.size[0], edgeVector.start[1]]
      ]);
    } else {
      lines.push([
        [edgeVector.start[0], boundingBox.point[1]],
        [edgeVector.start[0], boundingBox.point[1] + boundingBox.size[1]]
      ]);
    }
  });
  let midX;
  let midY;
  if (boundsVectors.length === 2) {
    const [edgeVectorsA, edgeVectorsB] = boundsVectors;
    const edgeVectorsADict = edgeVectorsToDict(edgeVectorsA);
    const edgeVectorsBDict = edgeVectorsToDict(edgeVectorsB);
    if (edgeVectorsADict.right.start[0] < edgeVectorsBDict.left.start[0]) {
      [midX] = PointUtils.mid(
        edgeVectorsADict.right.start,
        edgeVectorsBDict.left.start
      );
      lines.push([
        [midX, boundingBox.point[1]],
        [midX, boundingBox.point[1] + boundingBox.size[1]]
      ]);
    } else if (edgeVectorsADict.left.start[0] > edgeVectorsBDict.right.start[0]) {
      [midX] = PointUtils.mid(
        edgeVectorsADict.left.start,
        edgeVectorsBDict.right.start
      );
      lines.push([
        [midX, boundingBox.point[1]],
        [midX, boundingBox.point[1] + boundingBox.size[1]]
      ]);
    }
    if (edgeVectorsADict.bottom.start[1] < edgeVectorsBDict.top.start[1]) {
      [, midY] = PointUtils.mid(
        edgeVectorsADict.bottom.start,
        edgeVectorsBDict.top.start
      );
      lines.push([
        [boundingBox.point[0], midY],
        [boundingBox.point[0] + boundingBox.size[0], midY]
      ]);
    } else if (edgeVectorsADict.top.start[1] > edgeVectorsBDict.bottom.start[1]) {
      [, midY] = PointUtils.mid(
        edgeVectorsADict.top.start,
        edgeVectorsBDict.bottom.start
      );
      lines.push([
        [boundingBox.point[0], midY],
        [boundingBox.point[0] + boundingBox.size[0], midY]
      ]);
    }
  }
  entryPoints.forEach(({ sides, point }) => {
    sides.forEach((side) => {
      if (side === Side.Left || side === Side.Right) {
        const width = side === Side.Right ? boundingBox.size[0] : 0;
        lines.push([point, [boundingBox.point[0] + width, point[1]]]);
      } else {
        const height = side === Side.Bottom ? boundingBox.size[1] : 0;
        lines.push([point, [point[0], boundingBox.point[1] + height]]);
      }
    });
  });
  const detectIntersections = bush(
    lines.map((line) => ({
      from: {
        x: line[0][0],
        y: line[0][1]
      },
      to: {
        x: line[1][0],
        y: line[1][1]
      }
    })),
    {}
  );
  const intersections = detectIntersections.run();
  const points = intersections.map((intersection) => [
    intersection.point.x,
    intersection.point.y
  ]);
  lines.forEach((line) => {
    points.push(line[0], line[1]);
  });
  points.push(startPoint, endPoint);
  const isSameObject = !ConnectorUtils.isPoint(start) && !ConnectorUtils.isPoint(end) && start.id === end.id;
  const graph = pointsToGraph(
    points,
    startPoint,
    endPoint,
    illegalIntersects,
    illegalAreas,
    penaltyAreas,
    isSameObject,
    midX,
    midY
  );
  return graph;
}
function pointsToGraph(points, startPoint, endPoint, illegalIntersects = [], illegalAreas = [], penaltyAreas = [], isSameObject = false, midX, midY) {
  const xValues = /* @__PURE__ */ new Set();
  const yValues = /* @__PURE__ */ new Set();
  const graph = createGraph();
  points.forEach((point) => {
    const isStartPoint = startPoint && PointUtils.isCloseTo(point, startPoint);
    const isEndPoint = endPoint && PointUtils.isCloseTo(point, endPoint);
    const isStartOrEnd = isStartPoint || isEndPoint;
    if (!isStartOrEnd) {
      if (illegalAreas.some((area) => RectangleUtils.contains(area, point, true)))
        return;
    }
    const [x, y] = point;
    xValues.add(x);
    yValues.add(y);
    const isMid = midX !== void 0 && NumberUtils.isCloseTo(x, midX) || midY !== void 0 && NumberUtils.isCloseTo(y, midY);
    graph.addNode(pointId(x, y), { point, isMid });
  });
  function lineIntersectsEdgeVector(pointA, pointB, edgeVector) {
    return PointUtils.lineIntersectsLine(
      pointA,
      pointB,
      edgeVector.start,
      edgeVector.end
    );
  }
  const isLegalLink = (pointA, pointB) => {
    if (isSameObject) {
      const isStartAndEnd = (PointUtils.isCloseTo(pointA, startPoint) || PointUtils.isCloseTo(pointA, endPoint)) && (PointUtils.isCloseTo(pointB, startPoint) || PointUtils.isCloseTo(pointB, endPoint));
      if (isStartAndEnd)
        return false;
    }
    return !illegalIntersects.some((illegalEdgeVector) => {
      const intersection = lineIntersectsEdgeVector(
        pointA,
        pointB,
        illegalEdgeVector
      );
      if (intersection === null)
        return false;
      if (PointUtils.isCloseTo(intersection, pointA) || PointUtils.isCloseTo(intersection, pointB)) {
        const inA = PointUtils.extend(pointA, pointB, -1);
        if (illegalAreas.some((area) => RectangleUtils.contains(area, inA)))
          return true;
        const inB = PointUtils.extend(pointB, pointA, -1);
        if (illegalAreas.some((area) => RectangleUtils.contains(area, inB)))
          return true;
        return false;
      }
      return true;
    });
  };
  const PENALTY_AREA_COEFFICIENT = 4;
  function addLink(nodeA, nodeB) {
    if (!isLegalLink(nodeA.data.point, nodeB.data.point))
      return;
    const aPenalty = penaltyAreas.some(
      (area) => RectangleUtils.contains(area, nodeA.data.point, true)
    ) ? PENALTY_AREA_COEFFICIENT : 1;
    const bPenalty = penaltyAreas.some(
      (area) => RectangleUtils.contains(area, nodeA.data.point, true)
    ) ? PENALTY_AREA_COEFFICIENT : 1;
    const baseCoefficient = nodeA.data.isMid && nodeB.data.isMid ? 0.75 : 1;
    graph.addLink(nodeA.id, nodeB.id, {
      coefficient: baseCoefficient * bPenalty
    });
    graph.addLink(nodeB.id, nodeA.id, {
      coefficient: baseCoefficient * aPenalty
    });
  }
  const sortedXValues = Array.from(xValues).sort((a, b) => a - b);
  const sortedYValues = Array.from(yValues).sort((a, b) => a - b);
  for (let i = 0; i < sortedYValues.length; i++) {
    for (let j = 0; j < sortedXValues.length; j++) {
      const x = sortedXValues[j];
      const y = sortedYValues[i];
      const currentId = pointId(x, y);
      const currentNode = graph.getNode(currentId);
      if (!currentNode)
        continue;
      for (let k = j - 1; k >= 0; k--) {
        const previousXId = pointId(sortedXValues[k], y);
        const previousXNode = graph.getNode(previousXId);
        if (previousXNode) {
          addLink(previousXNode, currentNode);
          break;
        }
      }
      for (let k = i - 1; k >= 0; k--) {
        const previousYId = pointId(x, sortedYValues[k]);
        const previousYNode = graph.getNode(previousYId);
        if (previousYNode) {
          addLink(previousYNode, currentNode);
          break;
        }
      }
    }
  }
  return graph;
}
function pointId(x, y) {
  return `${Number(x.toFixed(5))},${Number(y.toFixed(5))}`;
}
function route(design, connector, startPoint, endPoint, isStart = true, isEnd = true, entryAngle, noZigzag = false) {
  const { start, end } = connector;
  const startIsPoint = !isStart || ConnectorUtils.isPoint(start);
  const endIsPoint = !isEnd || ConnectorUtils.isPoint(end);
  const totalDelta = PointUtils.subtract(endPoint, startPoint);
  const ids = /* @__PURE__ */ new Set();
  [start, end].forEach((connection) => {
    if (!ConnectorUtils.isPoint(connection)) {
      ids.add(connection.id);
    }
  });
  const boundsList = mapFilterNullish(Array.from(ids), (id) => {
    return design.getBounds(id, true);
  });
  const isInside = boundsList.some(
    (bounds) => RectangleUtils.contains(bounds, startPoint, true) || RectangleUtils.contains(bounds, endPoint, true)
  );
  const isTouchingAllBounds = boundsList.length > 1 && [startPoint, endPoint].some(
    (point) => boundsList.every((bounds) => RectangleUtils.contains(bounds, point))
  );
  if (startIsPoint && endIsPoint || isInside || isTouchingAllBounds) {
    if (!noZigzag)
      return {
        points: [
          startPoint,
          ...zigzag(
            startPoint,
            endPoint,
            Math.abs(totalDelta[0]) >= Math.abs(totalDelta[1])
          ),
          endPoint
        ]
      };
  }
  const startAngle = startIsPoint ? void 0 : sideToNormalAngle[start.side];
  const endAngle = endIsPoint ? void 0 : oppositeAngle(sideToNormalAngle[end.side]);
  const deltaAngle = startAngle === void 0 || endAngle === void 0 ? void 0 : angleDiff(startAngle, endAngle);
  const angleBound = Math.PI / 2 + NumberUtils.EPSILON;
  const obstacleArea = boundsList.length ? RectangleUtils.uniteAll(boundsList) : null;
  const obstacles = getObstacles(design, connector, obstacleArea, ids);
  const edgeVectors = boundsList.reduce((acc, bounds) => {
    acc.push(
      ...RectangleUtils.getEdgeVectors(bounds, {
        edges: { top: true, left: true, right: true, bottom: true }
      })
    );
    return acc;
  }, []);
  const lineIntersectsObject = /* @__PURE__ */ (() => {
    let result;
    return () => {
      if (result !== void 0)
        return result;
      result = edgeVectors.some((edgeVector) => {
        if (result !== void 0)
          return result;
        const intersection = PointUtils.lineIntersectsLine(
          edgeVector.start,
          edgeVector.end,
          startPoint,
          endPoint
        );
        if (!intersection)
          return false;
        if (PointUtils.isCloseTo(intersection, startPoint) || PointUtils.isCloseTo(intersection, endPoint))
          return false;
        return true;
      });
      return result;
    };
  })();
  if (startIsPoint || endIsPoint) {
    const angleWithin90Degrees = deltaAngle === void 0 || deltaAngle <= angleBound && deltaAngle >= -angleBound;
    if (angleWithin90Degrees) {
      if (!lineIntersectsObject()) {
        const path2 = getSimplePath(
          startPoint,
          endPoint,
          startAngle,
          endAngle,
          deltaAngle,
          entryAngle,
          noZigzag
        );
        const area = RectangleUtils.containing(path2);
        if (obstacles.every(
          (obstacle) => !RectangleUtils.intersects(area, obstacle)
        )) {
          return { points: path2 };
        }
      }
    }
  } else if (start.id !== end.id) {
    if (!lineIntersectsObject()) {
      const isParallel = sideIsParallel(start.side, end.side);
      const horizontalFirst = !sideIsHorizontal(start.side);
      const path2 = isParallel ? zigzag(startPoint, endPoint, horizontalFirst) : rightAngle(startPoint, endPoint, horizontalFirst);
      const area = RectangleUtils.containing(path2);
      if (obstacles.every(
        (obstacle) => !RectangleUtils.intersects(area, obstacle)
      )) {
        return { points: path2 };
      }
    }
  }
  const graph = createRouteGraph(
    design,
    connector,
    startPoint,
    endPoint,
    obstacles,
    void 0,
    isStart,
    isEnd
  );
  const startNode = graph.getNode(pointId(...startPoint));
  const endNode = graph.getNode(pointId(...endPoint));
  if (!startNode || !endNode) {
    return { points: [] };
  }
  function manhattanDistance(fromNode, toNode) {
    const dx = fromNode.data.point[0] - toNode.data.point[0];
    const dy = fromNode.data.point[1] - toNode.data.point[1];
    return Math.abs(dx) + Math.abs(dy);
  }
  const totalDistance = manhattanDistance(startNode, endNode);
  const distance = (toNode, fromNode, link, parent) => {
    var _a, _b;
    let penalty = 0;
    const penaltyCoefficient = (_a = link.data.coefficient) != null ? _a : 1;
    if (parent) {
      const parentPoint = parent.data.point;
      const fromPoint = fromNode.data.point;
      const toPoint = toNode.data.point;
      const fromHorizontal = NumberUtils.isCloseTo(
        parentPoint[0],
        fromPoint[0]
      );
      const toHorizontal = NumberUtils.isCloseTo(fromPoint[0], toPoint[0]);
      if (fromHorizontal !== toHorizontal)
        penalty += totalDistance * 0.2;
    }
    penalty += (_b = link.data.cost) != null ? _b : 0;
    return manhattanDistance(fromNode, toNode) * penaltyCoefficient + penalty;
  };
  const path = nba(graph, {
    oriented: true,
    distance,
    heuristic: manhattanDistance
  }).find(endNode.id, startNode.id);
  return {
    points: path.map((node) => [node.data.point[0], node.data.point[1]]),
    graph
  };
}
function getSimplePath(startPoint, endPoint, startAngle, endAngle, deltaAngle, entryAngle, noZigzag = false) {
  if (!noZigzag) {
    if (deltaAngle === void 0 || NumberUtils.isCloseTo(0, deltaAngle)) {
      const horizontalFirst2 = shouldHorizontalFirst(
        startPoint,
        endPoint,
        startAngle,
        endAngle,
        entryAngle,
        false
      );
      return zigzag(startPoint, endPoint, horizontalFirst2);
    }
  }
  const horizontalFirst = shouldHorizontalFirst(
    startPoint,
    endPoint,
    startAngle,
    endAngle,
    entryAngle,
    true
  );
  return rightAngle(startPoint, endPoint, horizontalFirst);
}
function shouldHorizontalFirst(startPoint, endPoint, startAngle, endAngle, entryAngle, perpendicular = false) {
  if (startAngle !== void 0) {
    return isHorizontal(startAngle);
  }
  if (endAngle !== void 0) {
    const endAngleIsHorizontal = isHorizontal(endAngle);
    return perpendicular ? !endAngleIsHorizontal : endAngleIsHorizontal;
  }
  if (entryAngle === void 0) {
    return true;
  }
  const horizontalAngle = startPoint[0] < endPoint[0] ? 0 : Math.PI;
  const verticalAngle = startPoint[1] < endPoint[1] ? Math.PI / 2 : Math.PI * 1.5;
  if (entryAngle !== void 0) {
    const normalizedEntryAngle = normalizeRadians(entryAngle);
    if (NumberUtils.isCloseTo(normalizedEntryAngle, horizontalAngle)) {
      return true;
    }
    if (NumberUtils.isCloseTo(normalizedEntryAngle, verticalAngle)) {
      return false;
    }
    const opposite = oppositeAngle(normalizedEntryAngle);
    if (NumberUtils.isCloseTo(opposite, horizontalAngle)) {
      return false;
    }
    if (NumberUtils.isCloseTo(opposite, verticalAngle)) {
      return true;
    }
  }
  return true;
}
function getObstacles(design, connector, bounds, ignoredIds) {
  var _a;
  if (!connector.avoidCollisions || !bounds)
    return [];
  const expandedBounds = RectangleUtils.expand(
    bounds,
    ((_a = connector.radius) != null ? _a : 16) * 4
  );
  return mapFilterNullish(
    [...design.walk(void 0, { maxDepth: 1 })],
    ([item]) => {
      if (ignoredIds.has(item.id))
        return null;
      if (item.className === ItemClass.Connector)
        return null;
      const obstacleBounds = design.getBounds(item.id, true);
      if (!obstacleBounds)
        return null;
      if (!RectangleUtils.intersects(expandedBounds, obstacleBounds))
        return null;
      return obstacleBounds;
    }
  );
}
function zigzag(startPoint, endPoint, horizontalFirst = true) {
  const delta = PointUtils.subtract(endPoint, startPoint);
  if (horizontalFirst) {
    return [
      startPoint,
      [startPoint[0] + delta[0] / 2, startPoint[1]],
      [startPoint[0] + delta[0] / 2, endPoint[1]],
      endPoint
    ];
  }
  return [
    startPoint,
    [startPoint[0], startPoint[1] + delta[1] / 2],
    [endPoint[0], startPoint[1] + delta[1] / 2],
    endPoint
  ];
}
function rightAngle(startPoint, endPoint, horizontalFirst = true) {
  const delta = PointUtils.subtract(endPoint, startPoint);
  if (horizontalFirst) {
    return [startPoint, [startPoint[0] + delta[0], startPoint[1]], endPoint];
  }
  return [startPoint, [startPoint[0], startPoint[1] + delta[1]], endPoint];
}
var sideToNormalAngle = {
  [Side.Top]: Math.PI / 2,
  [Side.Right]: 0,
  [Side.Bottom]: -Math.PI / 2,
  [Side.Left]: Math.PI
};
var PI_2 = Math.PI * 2;
function oppositeAngle(angle) {
  return (angle + Math.PI) % PI_2;
}
function angleDiff(a, b) {
  const diff = a - b;
  if (diff > Math.PI) {
    return diff - PI_2;
  }
  if (diff < -Math.PI) {
    return diff + PI_2;
  }
  return diff;
}
function normalizeRadians(angle) {
  return (angle % PI_2 + PI_2) % PI_2;
}
function isHorizontal(angle) {
  const absAngle = normalizeRadians(angle);
  return NumberUtils.isCloseTo(absAngle, Math.PI) || NumberUtils.isCloseTo(absAngle, 0);
}
function sideIsHorizontal(side) {
  return side === Side.Top || side === Side.Bottom;
}
function sideIsParallel(sideA, sideB) {
  return sideIsHorizontal(sideA) === sideIsHorizontal(sideB);
}

// src/PluginConnection.ts
import { makeAutoObservable } from "mobx";
var PluginConnection = class {
  constructor({ design }) {
    /**
     * The ID of the connector that is currently being edited
     */
    this.connectorId = null;
    /**
     * Which element and side the connector is currently targeting
     */
    this.target = null;
    /**
     * Whether the connector is currently being dragged
     */
    this.active = false;
    this._design = design;
    makeAutoObservable(this, {
      _design: false
    });
  }
  set design(design) {
    this._design = design;
  }
};

// src/react.tsx
import { useDesign } from "@ballpark/design-plugin-react";
import { runInAction } from "mobx";
import {
  createContext,
  useContext,
  useEffect,
  useState
} from "react";
import { useMemoOne } from "use-memo-one";
import { Fragment, jsx } from "react/jsx-runtime";
var PluginConnectionContext = createContext(null);
function useConnection() {
  const state = useContext(PluginConnectionContext);
  if (state === null)
    throw new Error("useConnection() called outside of ConnectionProvider");
  return state;
}
function useWeakConnection() {
  const maybePluginConnection = useContext(PluginConnectionContext);
  const design = useDesign();
  const pluginConnection = useMemoOne(
    () => maybePluginConnection != null ? maybePluginConnection : new PluginConnection({ design }),
    [maybePluginConnection, design]
  );
  return pluginConnection;
}
function ConnectionProvider({
  children,
  options
}) {
  const design = useDesign();
  const [pluginConnection] = useState(
    () => new PluginConnection(__spreadValues({ design }, options))
  );
  useEffect(() => {
    runInAction(() => {
      if (design)
        pluginConnection.design = design;
    });
  }, [design, pluginConnection]);
  return /* @__PURE__ */ jsx(PluginConnectionContext.Provider, { value: pluginConnection, children });
}
function WeakConnectionProvider({
  children,
  options
}) {
  const state = useContext(PluginConnectionContext);
  if (state === null) {
    return /* @__PURE__ */ jsx(ConnectionProvider, { options, children });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}

// src/utils.ts
import {
  NumberUtils as NumberUtils2,
  PointUtils as PointUtils2
} from "@ballpark/design-creator";
var CONTROL_HANDLE_RATIO = 0.5522847498;
function arcCorners(points, targetRadius = 0) {
  if (targetRadius === 0)
    return points;
  if (points.length < 3)
    return points;
  const result = [points[0]];
  for (let i = 0; i < points.length - 2; i++) {
    const pointA = points[i];
    const pointAB = points[i + 1];
    const pointBC = points[i + 2];
    const isAHorizontal = NumberUtils2.isCloseTo(pointA[1], pointAB[1]);
    const isBHorizontal = NumberUtils2.isCloseTo(pointAB[1], pointBC[1]);
    const lengthAB = PointUtils2.distance(pointA, pointAB);
    const lengthBC = PointUtils2.distance(pointAB, pointBC);
    const maxRadius = Math.min(lengthAB, lengthBC) / 2;
    const radius = Math.min(targetRadius, maxRadius);
    const curveStart = PointUtils2.extend(pointA, pointAB, -radius);
    const curveEnd = PointUtils2.extend(pointBC, pointAB, -radius);
    const curveStartDelta = PointUtils2.subtract(pointAB, curveStart);
    const curveEndDelta = PointUtils2.subtract(pointAB, curveEnd);
    const controlPointA = isAHorizontal ? [
      curveStart[0] + curveStartDelta[0] * CONTROL_HANDLE_RATIO,
      curveStart[1]
    ] : [
      curveStart[0],
      curveStart[1] + curveStartDelta[1] * CONTROL_HANDLE_RATIO
    ];
    const controlPointB = isBHorizontal ? [curveEnd[0] + curveEndDelta[0] * CONTROL_HANDLE_RATIO, curveEnd[1]] : [curveEnd[0], curveEnd[1] + curveEndDelta[1] * CONTROL_HANDLE_RATIO];
    result.push([
      curveStart[0],
      curveStart[1],
      curveStart[0],
      curveStart[1],
      controlPointA[0],
      controlPointA[1]
    ]);
    result.push([
      curveEnd[0],
      curveEnd[1],
      controlPointB[0],
      controlPointB[1],
      curveEnd[0],
      curveEnd[1]
    ]);
  }
  result.push(points[points.length - 1]);
  return result;
}
function offsetPoints(points, startOffset = 0, endOffset = 0) {
  if (points.length < 2)
    return points;
  return [
    startOffset !== 0 ? PointUtils2.extend(points[1], points[0], -startOffset) : points[0],
    ...points.slice(1, points.length - 1),
    endOffset !== 0 ? PointUtils2.extend(
      points[points.length - 2],
      points[points.length - 1],
      -endOffset
    ) : points[points.length - 1]
  ];
}
function normalizePoints(points) {
  if (!points.length)
    return points;
  const filteredPoints = [points[0]];
  let slope = null;
  for (let i = 1; i < points.length; i++) {
    const previousPoint = filteredPoints[filteredPoints.length - 1];
    const point = points[i];
    if (!PointUtils2.isCloseTo(previousPoint, point)) {
      const newSlope = PointUtils2.slope(previousPoint, point);
      if (slope !== null && NumberUtils2.isCloseTo(newSlope, slope)) {
        filteredPoints.pop();
      }
      filteredPoints.push(point);
      slope = newSlope;
    }
  }
  return filteredPoints;
}
function getLineSegmentsFromPolyline(points, segmentOffset, segmentOffsetType, overflow = false) {
  if (points.length < 2)
    return null;
  if (points.length === 2)
    return points;
  if (segmentOffsetType === "absolute") {
    if (segmentOffset < 0) {
      const segmentIndex2 = Math.max(1, points.length + segmentOffset);
      if (overflow)
        return points.slice(segmentIndex2 - 1);
      return [points[segmentIndex2 - 1], points[segmentIndex2]];
    }
    const segmentIndex = Math.min(points.length - 2, segmentOffset);
    if (overflow)
      return points.slice(segmentIndex);
    return [points[segmentIndex], points[segmentIndex + 1]];
  }
  return getLineSegmentAtPolylineOffset(points, segmentOffset);
}
function computeLabelPosition(points, labelPosition, scale = 1, buffer = [0, 0]) {
  const {
    offset,
    offsetType,
    segment: segmentOffset,
    segmentType
  } = labelPosition;
  const isAbsolute = offsetType === "absolute";
  const targetPolyline = segmentOffset === void 0 ? points : getLineSegmentsFromPolyline(
    points,
    isAbsolute ? segmentOffset * scale : segmentOffset,
    segmentType,
    isAbsolute
  );
  if (!targetPolyline)
    return null;
  return getPointAtPolylineOffset(
    targetPolyline,
    isAbsolute ? offset * scale : offset,
    offsetType,
    buffer
  );
}
function getLineSegmentAtPolylineOffset(points, offset = 0.5, overflow = false) {
  if (points.length < 2)
    return null;
  const length = PointUtils2.getPolylineLength(points);
  const targetLength = length * offset;
  let currentLength = 0;
  for (let i = 1; i < points.length; i++) {
    const previousPoint = points[i - 1];
    const point = points[i];
    const segmentLength = PointUtils2.distance(previousPoint, point);
    if (currentLength + segmentLength > targetLength) {
      if (overflow)
        return points.slice(i - 1);
      return [previousPoint, point];
    }
    currentLength += segmentLength;
  }
  return [points[points.length - 2], points[points.length - 1]];
}
function getPointAtPolylineOffset(points, offset = 0.5, offsetType = "relative", buffer = [0, 0]) {
  if (points.length < 2)
    return null;
  const targetLength = offsetType === "relative" ? PointUtils2.getPolylineLength(points) * offset : offset;
  let currentLength = 0;
  for (let i = 1; i < points.length; i++) {
    const previousPoint = points[i - 1];
    const point = i === 1 ? bufferSegment([points[0], points[1]], buffer)[1] : points[i];
    const segmentLength = PointUtils2.distance(previousPoint, point);
    if (currentLength + segmentLength > targetLength) {
      const segmentOffset = (targetLength - currentLength) / segmentLength;
      return {
        segment: [previousPoint, point],
        point: PointUtils2.interpolate(previousPoint, point, segmentOffset),
        overflowed: i > 1
      };
    }
    currentLength += segmentLength;
  }
  return {
    segment: points.slice(-2),
    point: points.length === 2 ? bufferSegment(points, buffer)[1] : points[points.length - 1],
    overflowed: points.length > 2
  };
}
function bufferSegment(segment, buffer) {
  const isHorizontal2 = NumberUtils2.isCloseTo(segment[0][1], segment[1][1]);
  const extent = isHorizontal2 ? buffer[0] : buffer[1];
  return [segment[0], PointUtils2.extend(segment[0], segment[1], -extent)];
}
export {
  ConnectionProvider,
  PluginConnection,
  WeakConnectionProvider,
  arcCorners,
  computeLabelPosition,
  createRouteGraph,
  getLineSegmentAtPolylineOffset,
  getPointAtPolylineOffset,
  normalizePoints,
  offsetPoints,
  route,
  useConnection,
  useWeakConnection
};
